import { ApiError, BackendErrorCode } from '@/modules/common/api/errors';

/** Per-day validation error type returned by the backend (extend as needed). */
export enum TimeOffDayValidationType {
  Overlap = 'OVERLAP',
}

/** One day's validation error: the ISO date + the failure type. */
export interface TimeOffDayValidationError {
  /** ISO date string (e.g. "2026-06-01") of the offending day. */
  date: string;
  /** The validation failure for that day (raw string when unknown). */
  type: TimeOffDayValidationType | string;
}

/**
 * Shape of the `args` payload sent with backend error code 2808
 * (`TIME_OFF_REQUEST_DAY_VALIDATION_ERRORS`):
 *
 * ```json
 * { "errors": { "2026-06-01": { "type": "OVERLAP" }, ... } }
 * ```
 */
interface DayValidationArgs {
  errors: Record<string, { type?: string }>;
}

const isDayValidationArgs = (value: unknown): value is DayValidationArgs =>
  typeof value === 'object' &&
  value !== null &&
  'errors' in value &&
  typeof (value as DayValidationArgs).errors === 'object' &&
  (value as DayValidationArgs).errors !== null;

/**
 * If the error is the day-level time-off validation error (code 2808), returns
 * the per-day errors parsed from its structured `args`; otherwise `null`. Used by
 * the create flow to open the overlap modal instead of a generic toast.
 */
export const extractTimeOffDayValidationErrors = (
  error: unknown
): TimeOffDayValidationError[] | null => {
  if (!(error instanceof ApiError)) return null;
  if (error.backendCode !== BackendErrorCode.TIME_OFF_REQUEST_DAY_VALIDATION_ERRORS) return null;
  if (!isDayValidationArgs(error.backendArgsRaw)) return null;

  return Object.entries(error.backendArgsRaw.errors)
    .map(([date, detail]) => ({
      date,
      type: detail?.type ?? TimeOffDayValidationType.Overlap,
    }))
    .sort((a, b) => a.date.localeCompare(b.date));
};
